import { Graphics } from 'pixi.js';

// Lucide SVGs imported as raw strings (Parcel `bundle-text:` scheme).
// Lucide icons use stroke="currentColor" — we substitute at draw time.
import checkSvg     from 'bundle-text:lucide-static/icons/check.svg';
import ellipsisSvg  from 'bundle-text:lucide-static/icons/more-horizontal.svg';
import circleXSvg   from 'bundle-text:lucide-static/icons/circle-x.svg';
import circleSvg    from 'bundle-text:lucide-static/icons/circle.svg';
import trashSvg     from 'bundle-text:lucide-static/icons/trash-2.svg';
import rotateSvg    from 'bundle-text:lucide-static/icons/rotate-ccw.svg';
import plusSvg      from 'bundle-text:lucide-static/icons/plus.svg';
import xSvg         from 'bundle-text:lucide-static/icons/x.svg';
import panelRightSvg from 'bundle-text:lucide-static/icons/panel-right.svg';
import editSvg      from 'bundle-text:lucide-static/icons/pencil.svg';
import fileTextSvg  from 'bundle-text:lucide-static/icons/file-text.svg';
import sunSvg       from 'bundle-text:lucide-static/icons/sun.svg';
import moonSvg      from 'bundle-text:lucide-static/icons/moon.svg';

// Firework / celebration icons
import starSvg        from 'bundle-text:lucide-static/icons/star.svg';
import heartSvg       from 'bundle-text:lucide-static/icons/heart.svg';
import zapSvg         from 'bundle-text:lucide-static/icons/zap.svg';
import flameSvg       from 'bundle-text:lucide-static/icons/flame.svg';
import smileSvg       from 'bundle-text:lucide-static/icons/smile.svg';
import trophySvg      from 'bundle-text:lucide-static/icons/trophy.svg';
import sparklesSvg    from 'bundle-text:lucide-static/icons/sparkles.svg';
import partyPopperSvg from 'bundle-text:lucide-static/icons/party-popper.svg';

export const ICONS = {
  check:      checkSvg,
  ellipsis:   ellipsisSvg,
  circleX:    circleXSvg,
  circle:     circleSvg,
  trash:      trashSvg,
  rotate:     rotateSvg,
  plus:       plusSvg,
  x:          xSvg,
  panelRight: panelRightSvg,
  edit:       editSvg,
  fileText:   fileTextSvg,
  sun:        sunSvg,
  moon:       moonSvg,
  star:        starSvg,
  heart:       heartSvg,
  zap:         zapSvg,
  flame:       flameSvg,
  smile:       smileSvg,
  trophy:      trophySvg,
  sparkles:    sparklesSvg,
  partyPopper: partyPopperSvg,
} as const;

/** Icons used in the success-firework animation. */
export const FIREWORK_ICONS: IconName[] = [
  'star', 'heart', 'zap', 'flame', 'smile', 'trophy', 'sparkles', 'partyPopper',
];

export type IconName = keyof typeof ICONS;

const hex = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

// Cache: every theme toggle drives ~525 calls to colorize() (75 sprites × 7 buttons).
// Distinct (svg, color, strokeWidth) tuples are < 50 in practice, so a plain Map
// keyed on the combined string is plenty.
const colorizedCache = new Map<string, string>();

function colorize(svg: string, color: number, strokeWidth: number): string {
  const key = `${color}|${strokeWidth}|${svg}`;
  let out = colorizedCache.get(key);
  if (out === undefined) {
    out = svg
      .replace(/currentColor/g, hex(color))
      .replace(/stroke-width="2"/, `stroke-width="${strokeWidth}"`);
    colorizedCache.set(key, out);
  }
  return out;
}

/**
 * Draws a lucide SVG into a Graphics object, centered on (0,0) at `size` px.
 * Lucide icons are 24×24 with `currentColor` strokes — we sub the color in.
 */
export function drawIcon(
  gfx:         Graphics,
  svg:         string,
  color:       number,
  size:        number,
  strokeWidth = 2,
): void {
  gfx.clear();
  const colored = colorize(svg, color, strokeWidth);

  const scale = size / 24;
  gfx.scale.set(scale);
  gfx.position.set(-12 * scale, -12 * scale);
  gfx.svg(colored);
}

/** Returns a lucide SVG string ready for DOM injection (no color sub needed — `currentColor` inherits from CSS). */
export function iconSvg(name: IconName, sizePx = 16): string {
  return ICONS[name].replace(
    /width="24"\s+height="24"/,
    `width="${sizePx}" height="${sizePx}"`,
  );
}
